/**
 * Progress Component
 *
 * Visual indicator for progress/completion states.
 * - Three variants: default, success, warning, error
 * - Accessible with ARIA attributes
 * - Smooth transitions
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import * as React from 'react'
import * as ProgressPrimitive from '@radix-ui/react-progress'

export interface ProgressProps
  extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  /** Current progress value (0-100) */
  value?: number
  /** Visual variant based on status */
  variant?: 'default' | 'success' | 'warning' | 'error'
  /** Size of the progress bar */
  size?: 'sm' | 'md' | 'lg'
  /** Show percentage label */
  showLabel?: boolean
  /** Accessible label for screen readers */
  label?: string
}

const variantStyles = {
  default: 'bg-primary-600 dark:bg-primary-500',
  success: 'bg-green-600 dark:bg-green-500',
  warning: 'bg-yellow-500 dark:bg-yellow-400',
  error: 'bg-red-600 dark:bg-red-500',
}

const sizeStyles = {
  sm: 'h-1.5',
  md: 'h-2.5',
  lg: 'h-4',
}

export const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ value = 0, variant = 'default', size = 'md', showLabel, label, className, ...props }, ref) => {
  const safeValue = Math.min(100, Math.max(0, value))

  return (
    <div className="w-full">
      {showLabel && (
        <div className="flex justify-between mb-1">
          <span className="text-sm text-neutral-700 dark:text-neutral-300">
            {label || 'Progress'}
          </span>
          <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            {Math.round(safeValue)}%
          </span>
        </div>
      )}
      <ProgressPrimitive.Root
        ref={ref}
        className={`
          relative w-full overflow-hidden rounded-full
          bg-neutral-200 dark:bg-neutral-700
          ${sizeStyles[size]}
          ${className || ''}
        `}
        value={safeValue}
        aria-label={label || 'Progress'}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={safeValue}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className={`
            h-full transition-all duration-300 ease-out rounded-full
            ${variantStyles[variant]}
          `}
          style={{ width: `${safeValue}%` }}
        />
      </ProgressPrimitive.Root>
    </div>
  )
})

Progress.displayName = 'Progress'
